import { useEffect, useState } from "react";
import {
    Compass,
    ExternalLink,
    Loader2,
    Lock,
    Package,
    Sparkles,
} from "lucide-react";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";

/**
 * Affiliate Partners page — obsidian / gold theme.
 * Data is read from GET /api/affiliate-partners so admin's "Make Live" flips
 * take effect immediately on the public page without a redeploy.
 */

const TAG_STYLES = {
    amber: "bg-gold/15 text-gold border-gold/40",
    forest: "bg-emerald-900/40 text-emerald-200 border-emerald-500/40",
    plum: "bg-purple-900/40 text-purple-200 border-purple-400/40",
    rose: "bg-rose-900/40 text-rose-200 border-rose-400/40",
    ocean: "bg-cyan-900/40 text-cyan-200 border-cyan-400/40",
};

function PartnerCard({ p, i }) {
    const tagClasses = TAG_STYLES[p.tag_style] || TAG_STYLES.amber;
    const isLive = p.status === "live" && p.url;
    return (
        <article
            className="relative rounded-sm border border-gold/15 bg-forest-deep/60 backdrop-blur overflow-hidden rise-in group hover:border-gold/50 transition-colors duration-500"
            style={{ animationDelay: `${i * 90}ms` }}
            data-testid={`partner-card-${p.slug}`}
        >
            <div className="aspect-[16/9] relative bg-gradient-to-br from-forest to-forest-deep border-b border-gold/15 flex items-center justify-center overflow-hidden">
                <div
                    className="absolute inset-0 opacity-20"
                    style={{
                        backgroundImage:
                            "radial-gradient(circle at 30% 30%, rgba(212,160,75,0.35), transparent 55%)",
                    }}
                    aria-hidden
                />
                <span
                    className="font-serif text-6xl md:text-7xl text-gold/80 italic tracking-tight"
                    data-testid={`partner-monogram-${p.slug}`}
                >
                    {p.name
                        .split(" ")
                        .map((w) => w[0])
                        .join("")
                        .slice(0, 3)}
                </span>
                <span
                    className={`absolute top-3 right-3 text-[0.55rem] tracking-[0.3em] uppercase px-2.5 py-1 rounded-full border backdrop-blur-sm ${tagClasses}`}
                    data-testid={`partner-tag-${p.slug}`}
                >
                    {p.tag}
                </span>
                {isLive && (
                    <span
                        className="absolute top-3 left-3 text-[0.55rem] tracking-[0.3em] uppercase px-2.5 py-1 rounded-full bg-emerald-500 text-forest-deep font-semibold"
                        data-testid={`partner-live-badge-${p.slug}`}
                    >
                        Live
                    </span>
                )}
            </div>

            <div className="p-6">
                <h3 className="font-serif text-2xl text-ink leading-tight">
                    {p.name}
                </h3>
                <p className="text-xs italic text-gold/80 mt-1">
                    {p.tagline}
                </p>
                <p className="mt-3 text-sm text-slate-300 leading-relaxed">
                    {p.description}
                </p>

                {isLive ? (
                    <a
                        href={p.url}
                        target="_blank"
                        rel="noopener noreferrer sponsored"
                        data-testid={`partner-visit-${p.slug}`}
                        className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full border border-gold bg-gold text-forest-deep text-[0.65rem] tracking-[0.3em] uppercase font-semibold px-5 py-3 transition-colors hover:bg-gold-soft"
                    >
                        <ExternalLink size={11} /> Visit Partner
                    </a>
                ) : (
                    <button
                        type="button"
                        disabled
                        aria-disabled="true"
                        data-testid={`partner-visit-${p.slug}`}
                        className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full border border-gold/30 bg-forest/40 text-slate-400 text-[0.65rem] tracking-[0.3em] uppercase font-semibold px-5 py-3 cursor-not-allowed select-none"
                    >
                        <Lock size={11} /> Coming Soon
                    </button>
                )}
                <p className="mt-2 text-[0.6rem] tracking-[0.25em] uppercase text-slate-500 text-center">
                    {isLive ? "Affiliate link · disclosed" : "Affiliate link pending"}
                </p>
            </div>
        </article>
    );
}

export default function Partners() {
    const [partners, setPartners] = useState(null);

    useEffect(() => {
        api.get("/affiliate-partners")
            .then(({ data }) => {
                // Partner Trust Standard · Director K ruling:
                //   Green  → listed publicly (the only trust signal shown)
                //   Yellow → STAYS LIVE publicly but internally "closely watched"
                //   Red    → removed from the public listing entirely
                // The public page carries no colour badges, no "curated"
                // qualifiers, no rating labels. Appearing on the page IS the
                // trust signal — kept intentionally defamation-safe.
                const visible = (data.partners || []).filter(
                    (p) => p.rating !== "red",
                );
                setPartners(visible);
            })
            .catch(() => setPartners([]));
    }, []);

    const dropship = (partners || []).filter((p) => p.section === "dropship");
    const affiliate = (partners || []).filter((p) => p.section === "affiliate");

    return (
        <div
            className="bg-forest-deep text-ink min-h-screen relative overflow-hidden"
            data-testid="partners-page"
        >
            <Seo
                title="Partners"
                description="A curated circle of complementary skincare brands we love — Hawaiian, Black-owned, Latina-owned, and clean-beauty makers who pair with the Island Gold Glow line."
            />

            <div
                className="absolute -top-40 -left-40 w-[680px] h-[680px] rounded-full blur-3xl opacity-15 pointer-events-none"
                style={{ background: "var(--ig-gold)" }}
                aria-hidden
            />
            <div className="grain absolute inset-0 pointer-events-none" />

            <section className="relative max-w-6xl mx-auto px-5 md:px-10 pt-20 pb-10">
                <span className="divider-rule text-gold">
                    <Compass size={12} /> Affiliate Partners
                </span>
                <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl leading-[0.98] text-ink mt-6">
                    Brands we love —
                    <br />
                    <span className="italic text-gold">that pair with ours.</span>
                </h1>
                <p className="mt-7 max-w-xl text-slate-300 leading-relaxed">
                    A small, hand-picked circle of complementary skincare
                    makers — Hawaiian, Black-owned, Latina-owned, and
                    clean-beauty. Tracking links are being set up now; cards go
                    live as each affiliate account is approved. All affiliate
                    relationships are disclosed; purchases through these links
                    may earn Island Gold a small commission.
                </p>
            </section>

            {partners == null ? (
                <div className="relative py-24 flex justify-center">
                    <Loader2 size={28} className="animate-spin text-gold" />
                </div>
            ) : (
                <>
                    {dropship.length > 0 && (
                        <section
                            className="relative max-w-6xl mx-auto px-5 md:px-10 pb-10"
                            data-testid="partners-dropship-section"
                        >
                            <div className="flex items-center gap-3 mb-6">
                                <Package size={14} className="text-gold" />
                                <h2 className="text-[0.65rem] tracking-[0.35em] uppercase text-gold font-semibold">
                                    Dropship Supplier
                                </h2>
                                <span className="flex-1 h-px bg-gold/15" aria-hidden />
                            </div>
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {dropship.map((p, i) => (
                                    <PartnerCard key={p.slug} p={p} i={i} />
                                ))}
                            </div>
                        </section>
                    )}

                    <section
                        className="relative max-w-6xl mx-auto px-5 md:px-10 pb-24"
                        data-testid="partners-affiliate-section"
                    >
                        <div className="flex items-center gap-3 mb-6 mt-6">
                            <Sparkles size={14} className="text-gold" />
                            <h2 className="text-[0.65rem] tracking-[0.35em] uppercase text-gold font-semibold">
                                Affiliate Partners · Skincare Circle
                            </h2>
                            <span className="flex-1 h-px bg-gold/15" aria-hidden />
                        </div>
                        <div
                            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
                            data-testid="partners-grid"
                        >
                            {affiliate.map((p, i) => (
                                <PartnerCard key={p.slug} p={p} i={i + 1} />
                            ))}
                        </div>

                        <p
                            className="mt-14 text-xs text-slate-400 text-center max-w-2xl mx-auto leading-relaxed"
                            data-testid="partners-disclosure"
                        >
                            <ExternalLink
                                size={11}
                                className="inline -mt-0.5 mr-1.5 text-gold/70"
                            />
                            <strong className="text-slate-200">FTC Affiliate Disclosure:</strong>{" "}
                            EIGOO earns a commission from partner purchases.
                            Every brand listed here is one we recommend —
                            Hawaiian, Pacific-aligned, and
                            independent-maker-forward.
                        </p>
                    </section>
                </>
            )}
        </div>
    );
}
